/**
 * board — the Firestore board document → what the Practice Ops views draw.
 *
 * WHY THIS EXISTS (2026-10-30, plan §6 / Phase 4). The classic page reads the document `practice-pulse/{siteID}`
 * inline in a 2,000-line component (PracticePulse.vue watcher `practicePulse`, ~2000–2150) and recomputes
 * waits from per-patient setInterval timers and a persisted `visualAlerts` map. Practice Ops draws the SAME
 * board four ways (Board, Wall, Operatory, Phone), so the reading is done ONCE here, as a pure function:
 *
 *     buildBoard(doc, { now, nameOf }) → {
 *       checkin:  [card],                                  sorted by arrival
 *       rooms:    [{ opNum, label, odName, cards:[card], occupied, orphan, lastLeftMs }],
 *       checkout: [card],                                  checked-out first (newest first), dismissed after
 *       labels:   { [opNum]: label },
 *       counts:   { checkin, arrived, ready, waiting, amber, red, over, inRooms, occupiedRooms, checkedOut, dismissed },
 *       thresholdMin: number | null,
 *       nowMs: number | null,
 *     }
 *
 * THE DOCUMENT. `doc.ops.checkin` is a MAP keyed by PatNum when the exe wrote it, and an ARRAY once the page has
 * saved a drag — both are read. `doc.ops.checkout` and every room (`doc.ops[<OperatoryNum>]`) are arrays; a map
 * is tolerated there too. `doc.settings` is the office's PRACTICE_PULSE_SETTINGS (allOps, opLabels, arrivedStatus
 * / readyStatus / dismissedStatus / inTreatmentRoomStatus {DefNum}, elapsedTimeAlert + elapsedTime). Entries that
 * are not objects, or have neither a PatNum nor an AptNum, are skipped; nothing else is ever dropped — a room
 * holding 2+ cards (old data, a race) lists all of them.
 *
 * OLD AND NEW DOCS. Old exes also wrote a snapshot of query results (recall, balance and the like) on every card, and
 * the page's `visualAlerts` map. Both are IGNORED here — alert icons come from Schedule View (plan §4) and waits
 * from timestamps. A slim doc with only the identity, status and time fields reads identically.
 *
 * THE CARD (normalised; every time is epoch ms or null):
 *   column      'checkin' | 'room' | 'checkout' — where the card is drawn (a drag reads it as the move's source)
 *   patNum aptNum provNum opNum(the room, null outside rooms) lastRoom(OperatoryNum the patient left, or null)
 *   scheduledMs(aptDateTime) arrivedMs(dateTimeArrived) readyMs(dateTimeReady) seatedMs(dateTimeTreatmentStarted)
 *   checkoutMs(dateTimeCheckout) dismissedMs(dateTimeDismissed) dismissed(bool)
 *   status      'arrived' | 'ready' | 'inTreatment' | 'dismissed'  (keys of the Wall's dot tokens, viewPref.js)
 *   waitMin     whole minutes waited. Check-in: the LIVE wait at `now`, null while the patient is EARLY or a time is
 *               unknown. Elsewhere: the completed wait (arrival→seated), informational.
 *   level       'ok' | 'amber' | 'red' — Check-in only; everywhere else, and whenever there is no wait, 'ok'
 *   early       Check-in: arrived before the booked time and it is not yet that time ("Early · appt 9:15")
 *   lateMin     whole minutes arrived after the booked time (0 on time / early, null unknown)
 *   inChairMin  rooms: minutes since seated; checkout: seated→checkout; else null
 *   tone        timer-chip tone for the card component: 'n' neutral | 'a' amber | 'r' red | 's' seated | 'd' dismissed
 *   key         unique within the board (a Vue :key)
 *   name first last                                  display name pieces (see `nameOf`)
 *   procDescript note provColor checkinColor checkinStatus checkinDefNum employeeID userNum   pass-through
 * ⛔ `note` can be clinical. The Wall must not draw it (plan: "Wall display drops the appointment note").
 *
 * WAITS COME FROM TIMESTAMPS + `now` ONLY (plan Phase 4): waitRules.js, with a `now` the caller passes. This file
 * never reads a clock, so a laptop that slept shows the right minutes the moment it is rebuilt. No stored
 * `visualAlerts` / badge field can make a card amber; the nightly reset's stale ones cannot survive.
 *
 * ROOMS. The rooms are `settings.allOps` in its stored order (what the classic board draws); if the settings carry
 * none, the document's own `allOps`; if there is no list at all, every numeric key of `doc.ops`. A room
 * NOT in the list but holding cards is appended (orphan: true) so no patient is ever invisible. The label is
 * `settings.opLabels[opNum]` when that is a non-blank string, else the Open Dental OpName, else "Room <opNum>".
 * PRACTICE OPS ONLY: `settings.opsLayout { order: [OperatoryNum], hidden: [OperatoryNum] }` (the Settings panel's Operatories
 * tab; classic never reads it and its `allOps` is never pruned or reordered). ABSENT ⇒ every room in the order above.
 * `order` puts the rooms it names first, in that order; every room it does not name follows in the order above (a room OD
 * added later is therefore appended, never lost). Ids that are not rooms (garbage, a deleted op) are ignored. `hidden`
 * drops a room from `rooms` — EXCEPT while it holds a patient: nobody is ever invisible. `labels` still names a hidden
 * room (a patient who left it is "last in <Room>").
 *
 * COUNTS. Header "Arrived N · Ready M": Ready = Check-in cards whose checkinDefNum is the office's
 * readyStatus.DefNum (the classic `howManyReady`); Arrived = the REST of Check-in (the design's
 * `inP.length - ready`; the classic `howManyArrived` counts arrivedStatus only, which is the same unless a card
 * carries some third status). `checkin` is the whole column. `over` = amber + red.
 *
 * NAMES. The exe writes no name; the classic page fills `name` from the roster. Pass `nameOf(card)` →
 * { first, last } | { FName, LName } | a string, and it is applied to every card that does not already carry them.
 *
 * TIMESTAMPS. Same readers as timestamps.js `toJsDate` (a Firestore Timestamp's toDate / {seconds,nanoseconds} /
 * {_seconds,_nanoseconds}, a Date, an ISO string, epoch ms) — COPIED here, not imported, because the files stay
 * import-free. Missing, blank or garbage ⇒ null; never NaN and never an Invalid Date.
 *
 * ⚠️ Keep this file import-free. The node-tests execute this exact file.
 */

const MS_PER_MINUTE = 60 * 1000;

// ── the wait rule (mirror of waitRules.js — cypress/node-tests/practice-ops-board.test.js pins them together) ──

function waitMinutes(scheduledMs, arrivedMs, endMs) {
  if (scheduledMs === null || arrivedMs === null || endMs === null) return null;
  return Math.max(0, Math.floor((endMs - Math.max(scheduledMs, arrivedMs)) / MS_PER_MINUTE));
}

function waitLevel(waitMin, thresholdMin) {
  if (typeof waitMin !== 'number' || !Number.isFinite(waitMin)) return 'ok';
  if (typeof thresholdMin !== 'number' || !Number.isFinite(thresholdMin) || thresholdMin <= 0) return 'ok';
  if (waitMin >= 2 * thresholdMin) return 'red';
  if (waitMin >= thresholdMin) return 'amber';
  return 'ok';
}

function minutesBetween(fromMs, toMs) {
  if (fromMs === null || toMs === null) return null;
  const diff = toMs - fromMs;
  return diff < 0 ? null : Math.floor(diff / MS_PER_MINUTE);
}

// ── readers ──────────────────────────────────────────────────────────────────────────────────────

/** Epoch ms from any shape a board timestamp takes, or null. Mirrors timestamps.js toJsDate. */
export function tsToMs(v) {
  if (v === null || v === undefined) return null;
  let ms = null;
  if (v instanceof Date) ms = v.getTime();
  else if (typeof v === 'number') ms = v;
  else if (typeof v === 'string') ms = v.trim() === '' ? NaN : Date.parse(v);
  else if (typeof v === 'object') {
    if (typeof v.toDate === 'function') {
      try {
        const d = v.toDate();
        ms = d instanceof Date ? d.getTime() : NaN;
      } catch (e) {
        ms = NaN;
      }
    } else {
      const seconds = typeof v.seconds === 'number' ? v.seconds : v._seconds;
      if (typeof seconds === 'number' && Number.isFinite(seconds)) {
        const nanos = typeof v.nanoseconds === 'number' ? v.nanoseconds
          : (typeof v._nanoseconds === 'number' ? v._nanoseconds : 0);
        ms = seconds * 1000 + Math.floor(nanos / 1e6);
      }
    }
  }
  return typeof ms === 'number' && Number.isFinite(ms) ? ms : null;
}

/** A whole number from a number or a digit string; else null. */
function toInt(v) {
  if (typeof v === 'number') return Number.isInteger(v) ? v : null;
  if (typeof v === 'string' && /^-?\d+$/.test(v.trim())) {
    const n = Number(v.trim());
    return Number.isSafeInteger(n) ? n : null;
  }
  return null;
}

function toPositiveInt(v) {
  const n = toInt(v);
  return n !== null && n > 0 ? n : null;
}

function text(v) {
  return typeof v === 'string' ? v.trim() : '';
}

function isObject(v) {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

/** Cards of one column as [rawCard, keyHint] pairs — an array, or a map keyed by PatNum. */
function entriesOf(column) {
  if (Array.isArray(column)) return column.map((raw) => [raw, null]);
  if (isObject(column)) return Object.keys(column).map((k) => [column[k], k]);
  return [];
}

/**
 * The wait alert's threshold in minutes, or null when the feature is off. Same rule as waitElapsed.js
 * `waitAlertEnabled`: BOTH `elapsedTimeAlert === 'TRUE'` and a positive `elapsedTime` (it has been stored as the
 * string "5"). An unticked box with an orphaned number is OFF.
 */
export function thresholdMinutes(settings) {
  if (!isObject(settings)) return null;
  if (settings.elapsedTimeAlert !== 'TRUE') return null;
  const limit = Number(settings.elapsedTime);
  return Number.isFinite(limit) && limit > 0 ? limit : null;
}

function defNumOf(settings, field) {
  return isObject(settings) && isObject(settings[field]) ? toInt(settings[field].DefNum) : null;
}

// ── cards ────────────────────────────────────────────────────────────────────────────────────────

function normaliseCard(raw, keyHint, ctx, where, opNum) {
  if (!isObject(raw)) return null;
  const patNum = toInt(raw.patNum) !== null ? toInt(raw.patNum) : toInt(keyHint);
  const aptNum = toInt(raw.aptNum);
  if (patNum === null && aptNum === null) return null;

  const scheduledMs = tsToMs(raw.aptDateTime);
  const arrivedMs = tsToMs(raw.dateTimeArrived);
  const readyMs = tsToMs(raw.dateTimeReady);
  const seatedMs = tsToMs(raw.dateTimeTreatmentStarted);
  const checkoutMs = tsToMs(raw.dateTimeCheckout);
  const dismissedMs = tsToMs(raw.dateTimeDismissed);
  const checkinDefNum = toInt(raw.checkinDefNum);
  const dismissed = dismissedMs !== null || (ctx.dismissedDef !== null && checkinDefNum === ctx.dismissedDef);

  let first = text(raw.first) || text(raw.fName) || text(raw.FName);
  let last = text(raw.last) || text(raw.lName) || text(raw.LName);
  if (!first && !last && typeof ctx.nameOf === 'function') {
    let looked = null;
    try { looked = ctx.nameOf({ patNum, aptNum, name: text(raw.name) }); } catch (e) { looked = null; }
    if (isObject(looked)) {
      first = text(looked.first) || text(looked.FName);
      last = text(looked.last) || text(looked.LName);
    }
  }
  const name = text(raw.name) || [first, last].filter(Boolean).join(' ');

  let status;
  if (dismissed) status = 'dismissed';
  else if (where === 'checkin') status = ctx.readyDef !== null && checkinDefNum === ctx.readyDef ? 'ready' : 'arrived';
  else status = 'inTreatment';

  const nowMs = ctx.nowMs;
  const early = where === 'checkin' && arrivedMs !== null && scheduledMs !== null && nowMs !== null
    && arrivedMs < scheduledMs && nowMs < scheduledMs;

  let waitMin;
  let level = 'ok';
  let inChairMin = null;
  if (where === 'checkin') {
    // a Check-in patient is not seated — a stale dateTimeTreatmentStarted (moved back) never ends the wait
    waitMin = early ? null : waitMinutes(scheduledMs, arrivedMs, nowMs);
    level = waitLevel(waitMin, ctx.thresholdMin);
  } else {
    waitMin = waitMinutes(scheduledMs, arrivedMs, seatedMs);
    inChairMin = minutesBetween(seatedMs, where === 'room' ? nowMs : checkoutMs);
  }

  let tone = 'n';
  if (where === 'checkin') tone = level === 'red' ? 'r' : (level === 'amber' ? 'a' : 'n');
  else if (dismissed) tone = 'd';
  else if (where === 'room') tone = 's';

  const lateDiff = scheduledMs !== null && arrivedMs !== null ? arrivedMs - scheduledMs : null;

  return {
    key: '',
    patNum,
    aptNum,
    provNum: toInt(raw.provNum),
    column: where,
    opNum: where === 'room' ? opNum : null,
    lastRoom: toPositiveInt(raw.lastRoom),
    scheduledMs,
    arrivedMs,
    readyMs,
    seatedMs,
    checkoutMs,
    dismissedMs,
    dismissed,
    status,
    waitMin,
    level,
    early,
    lateMin: lateDiff === null ? null : (lateDiff > 0 ? Math.floor(lateDiff / MS_PER_MINUTE) : 0),
    inChairMin,
    tone,
    name,
    first,
    last,
    procDescript: text(raw.procDescript),
    note: text(raw.note),
    provColor: toInt(raw.provColor),
    checkinColor: toInt(raw.checkinColor),
    checkinStatus: text(raw.checkinStatus),
    checkinDefNum,
    employeeID: typeof raw.employeeID === 'string' && raw.employeeID !== '' ? raw.employeeID : null,
    userNum: toInt(raw.userNum),
  };
}

function cardsOf(column, ctx, where, opNum) {
  const out = [];
  entriesOf(column).forEach(([raw, keyHint]) => {
    const card = normaliseCard(raw, keyHint, ctx, where, opNum);
    if (card) out.push(card);
  });
  return out;
}

/** Earliest arrival first; unknown arrival last; then booked time; then AptNum. */
function byArrival(a, b) {
  if (a.arrivedMs !== b.arrivedMs) {
    if (a.arrivedMs === null) return 1;
    if (b.arrivedMs === null) return -1;
    return a.arrivedMs - b.arrivedMs;
  }
  if (a.scheduledMs !== b.scheduledMs) {
    if (a.scheduledMs === null) return 1;
    if (b.scheduledMs === null) return -1;
    return a.scheduledMs - b.scheduledMs;
  }
  return (a.aptNum || 0) - (b.aptNum || 0);
}

/** Newest first on the given time; unknown last. */
function newestFirst(field) {
  return (a, b) => {
    if (a[field] === b[field]) return 0;
    if (a[field] === null) return 1;
    if (b[field] === null) return -1;
    return b[field] - a[field];
  };
}

// ── rooms ────────────────────────────────────────────────────────────────────────────────────────

function listedOps(doc, settings) {
  const list = Array.isArray(settings.allOps) && settings.allOps.length > 0 ? settings.allOps
    : (Array.isArray(doc.allOps) && doc.allOps.length > 0 ? doc.allOps : null);
  if (!list) return null;
  const seen = {};
  const out = [];
  list.forEach((op) => {
    if (!isObject(op)) return;
    const opNum = toPositiveInt(op.OperatoryNum);
    if (opNum === null || seen[opNum]) return;
    seen[opNum] = true;
    out.push({ opNum, odName: text(op.OpName) });
  });
  return out;
}

/**
 * The Practice-Ops-only layout: { order: [OperatoryNum...] (deduped, positive ints), hidden: [OperatoryNum...] }.
 * Anything that is not an object with those arrays reads as "no layout" (absent ⇒ every room, in the stored order).
 * Exported for the Settings panel, which edits the same two lists.
 */
export function readOpsLayout(settings) {
  const raw = isObject(settings) && isObject(settings.opsLayout) ? settings.opsLayout : null;
  const ids = (list) => {
    const seen = {};
    const out = [];
    (Array.isArray(list) ? list : []).forEach((v) => {
      const n = toPositiveInt(v);
      if (n === null || seen[n]) return;
      seen[n] = true;
      out.push(n);
    });
    return out;
  };
  return { order: ids(raw && raw.order), hidden: ids(raw && raw.hidden) };
}

/** `specs` with the rooms `order` names first (in that order); every other room keeps its relative position after them. */
function applyRoomOrder(specs, order) {
  if (!order.length) return specs;
  const byNum = {};
  specs.forEach((spec) => { byNum[spec.opNum] = spec; });
  const first = order.filter((n) => byNum[n]).map((n) => byNum[n]);
  const named = {};
  first.forEach((spec) => { named[spec.opNum] = true; });
  return first.concat(specs.filter((spec) => !named[spec.opNum]));
}

function labelFor(settings, opNum, odName) {
  const labels = isObject(settings.opLabels) ? settings.opLabels : {};
  return text(labels[opNum]) || odName || `Room ${opNum}`;
}

// ── the board ────────────────────────────────────────────────────────────────────────────────────

/**
 * @param {?object} doc  the `practice-pulse/{siteID}` document's data (null / garbage ⇒ an empty board)
 * @param {{now?: (number|Date|string|{toDate: Function}), nameOf?: Function}} [opts]
 *   now     the corrected "now" (clock.js). Missing ⇒ no live waits (every card 'ok', waitMin null).
 *   nameOf  see NAMES in the header
 */
export function buildBoard(doc, opts) {
  const { now, nameOf } = opts && typeof opts === 'object' ? opts : {};
  const board = isObject(doc) ? doc : {};
  const settings = isObject(board.settings) ? board.settings : {};
  const ops = isObject(board.ops) ? board.ops : {};

  const nowMs = tsToMs(now);
  const thresholdMin = thresholdMinutes(settings);
  const ctx = {
    nowMs,
    thresholdMin,
    nameOf,
    readyDef: defNumOf(settings, 'readyStatus'),
    dismissedDef: defNumOf(settings, 'dismissedStatus'),
  };

  const checkin = cardsOf(ops.checkin, ctx, 'checkin', null).sort(byArrival);

  const checkoutAll = cardsOf(ops.checkout, ctx, 'checkout', null);
  const checkout = checkoutAll.filter((c) => !c.dismissed).sort(newestFirst('checkoutMs'))
    .concat(checkoutAll.filter((c) => c.dismissed).sort(newestFirst('dismissedMs')));

  // rooms: the listed ops in order, then any unlisted room that holds patients
  const listed = listedOps(board, settings);
  const roomKeys = Object.keys(ops).filter((k) => k !== 'checkin' && k !== 'checkout' && toPositiveInt(k) !== null);

  const specs = [];
  const seen = {};
  if (listed) {
    listed.forEach((op) => { specs.push({ opNum: op.opNum, odName: op.odName, orphan: false }); seen[op.opNum] = true; });
  }
  roomKeys.map(Number).sort((a, b) => a - b).forEach((opNum) => {
    if (seen[opNum]) return;
    // with no op list at all every key is a room; with one, only an unlisted room that HOLDS someone is shown
    if (!listed || cardsOf(ops[opNum], ctx, 'room', opNum).length > 0) {
      specs.push({ opNum, odName: '', orphan: !!listed });
      seen[opNum] = true;
    }
  });

  // Practice Ops layout (settings.opsLayout): the order the office chose, then drop the rooms it hid — but a hidden room that
  // HOLDS a patient stays (nobody is invisible). Names are resolved for every room, hidden ones included.
  const layout = readOpsLayout(settings);
  const hidden = {};
  layout.hidden.forEach((n) => { hidden[n] = true; });

  const labels = {};
  const rooms = [];
  applyRoomOrder(specs, layout.order).forEach((spec) => {
    const cards = cardsOf(ops[spec.opNum], ctx, 'room', spec.opNum);
    const label = labelFor(settings, spec.opNum, spec.odName);
    labels[spec.opNum] = label;
    if (hidden[spec.opNum] && cards.length === 0) return;
    const left = checkoutAll.filter((c) => c.lastRoom === spec.opNum && c.checkoutMs !== null);
    rooms.push({
      opNum: spec.opNum,
      label,
      odName: spec.odName,
      cards,
      occupied: cards.length > 0,
      orphan: spec.orphan,
      lastLeftMs: left.length ? Math.max(...left.map((c) => c.checkoutMs)) : null,
    });
  });

  // unique keys (a Vue :key) — an array-form column can hold the same appointment twice
  const used = {};
  const keyAll = (cards, where) => cards.forEach((c) => {
    const base = `${where}-${c.aptNum !== null ? c.aptNum : `p${c.patNum}`}-${c.patNum}`;
    used[base] = (used[base] || 0) + 1;
    c.key = used[base] === 1 ? base : `${base}-${used[base]}`;
  });
  keyAll(checkin, 'in');
  rooms.forEach((r) => keyAll(r.cards, `op${r.opNum}`));
  keyAll(checkout, 'out');

  const ready = checkin.filter((c) => c.status === 'ready').length;
  const amber = checkin.filter((c) => c.level === 'amber').length;
  const red = checkin.filter((c) => c.level === 'red').length;
  const counts = {
    checkin: checkin.length,
    arrived: checkin.length - ready,
    ready,
    waiting: checkin.filter((c) => c.waitMin !== null).length,
    amber,
    red,
    over: amber + red,
    inRooms: rooms.reduce((n, r) => n + r.cards.length, 0),
    occupiedRooms: rooms.filter((r) => r.occupied).length,
    checkedOut: checkout.filter((c) => !c.dismissed).length,
    dismissed: checkout.filter((c) => c.dismissed).length,
  };

  return { checkin, rooms, checkout, labels, counts, thresholdMin, nowMs };
}
